import { Box } from '@mui/material';
import { useRef } from 'react';
import { Cell, Pie, PieChart as RechartsPieChart, Tooltip } from 'recharts';
import { useDelayedValue } from '../../hooks/useDelayedValue';
import { ChartTooltip } from './ChartTooltip';
import { PieActiveShape } from './PieActiveShape';

type GraphData = {
  amount: number;
  name: string;
  color: string;
};

type Props = {
  title?: string;
  data: GraphData[];
  radius: number;
  chartWidth: number;

  onMouseEnter: (index: number) => void;
  onMouseLeave: () => void;
  activeIndex?: number;
  total?: number | null;

  disableAnimation?: boolean;
};

export const PieChart = ({ title, data, radius, chartWidth, onMouseEnter, onMouseLeave, activeIndex, total = null, disableAnimation = false }: Props) => {
  const [renderChart] = useDelayedValue({ delay: disableAnimation ? 0 : 500 });
  const ref = useRef<HTMLElement | null>(null);

  const diameter = 2 * radius;

  let calculatedTotal = 0;
  if (total === null) {
    calculatedTotal = data.reduce((sum, { amount }) => sum + amount, 0);
  }

  return (
    <Box
      ref={ref}
      sx={{
        display: 'inline-block',
        width: `${diameter + 10}px`,
        height: `${diameter + 10}px`,
        '.recharts-surface': {
          position: 'relative',
          zIndex: 1,
        },
      }}
    >
      {!renderChart ? (
        <></>
      ) : (
        <RechartsPieChart
          margin={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
          width={diameter + 10}
          height={diameter + 10}
          aria-label={title}
        >
          <Pie
            cx={'50%'}
            cy={'50%'}
            data={data}
            innerRadius={radius - chartWidth}
            outerRadius={radius}
            dataKey="amount"
            nameKey="name"
            isAnimationActive={!disableAnimation}
            animationBegin={0}
            animationDuration={500}
            activeIndex={activeIndex}
            activeShape={PieActiveShape}
            onMouseEnter={(_, index) => onMouseEnter(index || 0)}
            onMouseLeave={() => onMouseLeave()}
            blendStroke
            aria-hidden={true}
            rootTabIndex={-1}
          >
            {data.map(({ color }, index) => (
              <Cell key={`cell-${index}`} fill={color} />
            ))}
          </Pie>
          <Tooltip
            position={{ x: 0, y: 0 }}
            wrapperStyle={{
              height: diameter + 10,
              width: diameter + 10,
              visibility: 'visible',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              flexDirection: 'column',
              pointerEvents: 'all',
              zIndex: 0,
            }}
            content={() => <ChartTooltip total={calculatedTotal} />}
          />
        </RechartsPieChart>
      )}
    </Box>
  );
};
